import { Set as ImmutableSet, type List } from 'immutable';

import { customersSelectors } from 'app-components/flux/customers';
import type { Customer } from 'app-components/flux/customers/customersTypes';
import { projectsSelectors } from 'app-components/flux/projects';
import { mkProject, type Project } from 'app-components/flux/projects/projectsTypes';
import store from 'app-components/utils/redux-store';

import * as chartOfAccountsSelectors from '../chart-of-accounts/chartOfAccountsSelectors';
import type { ChartOfAccountNodeProps, ChartOfAccountProps } from '../chart-of-accounts/chartOfAccountsTypes';
import * as chartOfAccountsTypes from '../chart-of-accounts/chartOfAccountsTypes';
import type {
  ReportConfigAdvancedFilterItemProps,
  ReportConfigChartOfAccountFilterItemProps,
  ReportConfigFilterProps,
  ReportConfigFilters,
} from '../report-configurations/reportConfigurationsTypes';
import { mkReportConfigFilters } from '../report-configurations/reportConfigurationsTypes';
import {
  AdvancedFilterObject,
  type CustomerAndProjects,
  type Filter,
  type FilterObject,
  type FilterProps,
  mkFilter,
  mkFilterObject,
} from './filtersTypes';

const TOP_LEVEL_CATEGORY_FILTERS = {
  'USAGE-PERSONAL': { id: '0', type: 'USAGE_TYPE' },
  'USAGE-BUSINESS': { id: '1', type: 'USAGE_TYPE' },
  TRANSFER: { id: '2', type: 'USAGE_TYPE' },
  OTHER: { id: '3', type: 'USAGE_TYPE' },
} as const;

type TopLevelCategoryFilterId = keyof typeof TOP_LEVEL_CATEGORY_FILTERS;
export type ChartOfAccountsTree = ReadonlyArray<ChartOfAccountNodeProps> | List<ChartOfAccountNodeProps>;
type ReportCategory = ReportConfigChartOfAccountFilterItemProps['chartOfAccounts'][number];

type ChartOfAccountIndex = {
  byNodeId: ReadonlyMap<string, ChartOfAccountNodeProps>;
  byCoa: ReadonlyMap<string, ChartOfAccountNodeProps>;
};

const TOP_LEVEL_CATEGORY_FORM_COAS: Record<TopLevelCategoryFilterId, ChartOfAccountProps> = {
  'USAGE-PERSONAL': { id: 'PERSONAL', type: 'CATEGORY_USAGE_TYPE' },
  'USAGE-BUSINESS': { id: 'BUSINESS', type: 'CATEGORY_USAGE_TYPE' },
  TRANSFER: { id: 'TRANSFER', type: 'DISPLAY_NODE' },
  OTHER: { id: 'OTHER', type: 'DISPLAY_NODE' },
};

const QCS_USAGE_TYPE_TO_FILTER_CATEGORY: Record<string, TopLevelCategoryFilterId> = {
  '0': 'USAGE-PERSONAL',
  '1': 'USAGE-BUSINESS',
  '2': 'TRANSFER',
  '3': 'OTHER',
};

const OTHER_TOP_LEVEL_CATEGORY_CHILD_IDS = [
  'UNCATEGORIZED',
  'BALANCE_ADJUSTMENT_0',
  'BALANCE_ADJUSTMENT_1',
  'BALANCE_ADJUSTMENT_3',
];

const isTopLevelCategoryFilterId = (id: string): id is TopLevelCategoryFilterId =>
  Object.prototype.hasOwnProperty.call(TOP_LEVEL_CATEGORY_FILTERS, id);

const isChartOfAccountProps = (
  item: ChartOfAccountProps | { id: string; type: 'USAGE_TYPE' },
): item is ChartOfAccountProps => item.type !== 'USAGE_TYPE';

const getChartOfAccountKey = ({ id, type }: ChartOfAccountProps): string => `${type}:${id}`;

const createChartOfAccountIndex = (coaNodes: ChartOfAccountsTree): ChartOfAccountIndex => {
  const byNodeId = new Map<string, ChartOfAccountNodeProps>();
  const byCoa = new Map<string, ChartOfAccountNodeProps>();
  const addNodes = (nodes: ChartOfAccountsTree): void => {
    nodes.forEach((node) => {
      if (node.id) byNodeId.set(node.id, node);
      if (node.coa) byCoa.set(getChartOfAccountKey(node.coa), node);
      if (node.children) addNodes(node.children);
    });
  };

  addNodes(coaNodes);
  return { byNodeId, byCoa };
};

const getChartOfAccountForFilterCategoryId = (
  categoryId: string,
  coaIndex: ChartOfAccountIndex,
): ChartOfAccountProps | undefined => {
  const node = coaIndex.byNodeId.get(categoryId);
  if (!node?.coa) {
    return undefined;
  }

  return {
    id: node.coa.id,
    type: node.coa.type,
  };
};

const isFilterCategoryUnderTopLevelCategory = (
  categoryId: string,
  topLevelCategoryId: TopLevelCategoryFilterId,
  coaIndex: ChartOfAccountIndex,
): boolean => {
  if (topLevelCategoryId === 'OTHER') {
    return OTHER_TOP_LEVEL_CATEGORY_CHILD_IDS.includes(categoryId);
  }

  const node = coaIndex.byNodeId.get(categoryId);
  return Boolean(node?.path?.includes(topLevelCategoryId));
};

const getTopLevelCategoryChildIds = (
  topLevelCategoryId: TopLevelCategoryFilterId,
  coaIndex: ChartOfAccountIndex,
): string[] => {
  if (topLevelCategoryId === 'OTHER') {
    return coaIndex.byNodeId.has('OTHER') ? OTHER_TOP_LEVEL_CATEGORY_CHILD_IDS : [];
  }

  return (
    coaIndex.byNodeId
      .get(topLevelCategoryId)
      ?.children?.map((item) => item.id)
      .toArray()
      .filter((id): id is string => typeof id === 'string') ?? []
  );
};

const getTopLevelCategoryDescendantIds = (
  topLevelCategoryId: TopLevelCategoryFilterId,
  coaIndex: ChartOfAccountIndex,
): string[] => {
  if (topLevelCategoryId === 'OTHER') {
    return coaIndex.byNodeId.has('OTHER') ? OTHER_TOP_LEVEL_CATEGORY_CHILD_IDS : [];
  }

  const descendantIds: string[] = [];
  const addDescendants = (children: NonNullable<ChartOfAccountNodeProps['children']>) => {
    children.forEach((child) => {
      if (child.id) {
        descendantIds.push(child.id);
      }
      if (child.children) {
        addDescendants(child.children);
      }
    });
  };

  const topLevelNode = coaIndex.byNodeId.get(topLevelCategoryId);
  if (topLevelNode?.children) {
    addDescendants(topLevelNode.children);
  }

  return descendantIds;
};

// web filter returns only an id which is the id of a category or a string name that needs to be mapped to an id
// also a type needs to be added to it
const categoryIdAndType = (
  catID: string,
  coaIndex: ChartOfAccountIndex,
): ChartOfAccountProps | { id: string; type: 'USAGE_TYPE' } => {
  if (isTopLevelCategoryFilterId(catID)) {
    return TOP_LEVEL_CATEGORY_FILTERS[catID];
  }

  const coa = getChartOfAccountForFilterCategoryId(catID, coaIndex);
  if (coa) {
    return coa;
  }

  switch (catID) {
    case chartOfAccountsTypes.CoaTypeEnum.UNCATEGORIZED:
      return { id: '0', type: chartOfAccountsTypes.CoaTypeEnum.UNCATEGORIZED };
    case 'BALANCE_ADJUSTMENT_0':
      return { id: '0', type: chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT };
    case 'BALANCE_ADJUSTMENT_1':
      return { id: '1', type: chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT };
    case 'BALANCE_ADJUSTMENT_2':
      return { id: '2', type: chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT };
    case 'BALANCE_ADJUSTMENT_3':
      return { id: '3', type: chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT };
    case '24':
      return { id: '24', type: chartOfAccountsTypes.CoaTypeEnum.ACCOUNT };
    default:
      return { id: catID, type: chartOfAccountsTypes.CoaTypeEnum.CATEGORY };
  }
};

const categoryIdFromQCSCategory = ({ id, type }: { id: string; type: string }): string => {
  if (type === 'ACCOUNT' || type === 'CATEGORY') {
    return id;
  }

  if (id === '0' && type === 'UNCATEGORIZED') {
    return 'UNCATEGORIZED';
  }
  if (id === '0' && type === 'BALANCE_ADJUSTMENT') {
    return 'BALANCE_ADJUSTMENT_0';
  }
  if (id === '1' && type === 'BALANCE_ADJUSTMENT') {
    return 'BALANCE_ADJUSTMENT_1';
  }
  if (id === '2' && type === 'BALANCE_ADJUSTMENT') {
    return 'BALANCE_ADJUSTMENT_2';
  }
  if (id === '3' && type === 'BALANCE_ADJUSTMENT') {
    return 'BALANCE_ADJUSTMENT_3';
  }
  return id;
};

const convertFilterCategoriesToQCSCategoriesWithIndex = (
  filterCategories: ImmutableSet<string>,
  useTopLevelCategoryFilters: boolean,
  coaIndex: ChartOfAccountIndex,
): ReportCategory[] => {
  const result: (ChartOfAccountProps | { id: string; type: 'USAGE_TYPE' })[] = [];
  const selectedCategories = filterCategories.toArray();
  const selectedTopLevelCategoryIds: TopLevelCategoryFilterId[] = [];
  const topLevelCategoriesToCheck = useTopLevelCategoryFilters
    ? (Object.keys(TOP_LEVEL_CATEGORY_FILTERS) as TopLevelCategoryFilterId[])
    : selectedCategories.filter(isTopLevelCategoryFilterId);
  const selectedTopLevelCategoryChildIds = new Set(
    topLevelCategoriesToCheck.flatMap((categoryId) => {
      const childIds = getTopLevelCategoryChildIds(categoryId, coaIndex);
      const selectedChildCount = childIds.filter((childId) => filterCategories.has(childId)).length;
      const parentSelected = filterCategories.has(categoryId);
      const shouldUseParentFilter =
        useTopLevelCategoryFilters &&
        ((childIds.length === 0 && parentSelected) || (childIds.length > 0 && selectedChildCount === childIds.length));

      if (shouldUseParentFilter) {
        selectedTopLevelCategoryIds.push(categoryId);
        result.push(TOP_LEVEL_CATEGORY_FILTERS[categoryId]);
        return childIds;
      }

      return [];
    }),
  );

  // We want to filter out container categories because these are meaningless in QCS and the reports/report-configuration
  // endpoint won't accept them. However, the reverse transform adds them back so the UI displays correctly.
  filterCategories &&
    result.push(
      ...filterCategories
        .map((cat) => categoryIdAndType(cat, coaIndex))
        .filter(
          (item) =>
            item.type !== 'USAGE_TYPE' &&
            (!useTopLevelCategoryFilters ||
              !(
                selectedTopLevelCategoryChildIds.has(categoryIdFromQCSCategory(item)) ||
                selectedTopLevelCategoryIds.some((categoryId) =>
                  isFilterCategoryUnderTopLevelCategory(categoryIdFromQCSCategory(item), categoryId, coaIndex),
                )
              )),
        ),
    );

  return result;
};

export const convertFilterCategoriesToQCSCategories = (
  filterCategories: ImmutableSet<string>,
  useTopLevelCategoryFilters: boolean,
  coaNodes: ChartOfAccountsTree,
): ReportCategory[] =>
  convertFilterCategoriesToQCSCategoriesWithIndex(
    filterCategories,
    useTopLevelCategoryFilters,
    createChartOfAccountIndex(coaNodes),
  );

const convertQCSCategoriesToFilterCategoriesWithIndex = (
  selectedCoas: ReadonlyArray<{ id: string; type: string }>,
  coaIndex: ChartOfAccountIndex,
): string[] => {
  // make approirate adjustments to the selectedCoas (reverse transform)
  const selectedCoaIds = selectedCoas.flatMap((it) => {
    if (it.type === 'USAGE_TYPE') {
      const topLevelCategoryId = QCS_USAGE_TYPE_TO_FILTER_CATEGORY[it.id];
      return topLevelCategoryId
        ? [topLevelCategoryId, ...getTopLevelCategoryDescendantIds(topLevelCategoryId, coaIndex)]
        : [];
    }
    return [categoryIdFromQCSCategory(it)];
  });
  return [...new Set(selectedCoaIds)];
};

export const convertQCSCategoriesToFilterCategories = (
  selectedCoas: ReadonlyArray<{ id: string; type: string }>,
  coaNodes: ChartOfAccountsTree,
): string[] => convertQCSCategoriesToFilterCategoriesWithIndex(selectedCoas, createChartOfAccountIndex(coaNodes));

const getFilterCategoryIdFromChartOfAccount = (coa: ChartOfAccountProps): string => {
  if (coa.type === 'CATEGORY_USAGE_TYPE') return `USAGE-${coa.id}`;
  if (coa.type === 'DISPLAY_NODE' && (coa.id === 'TRANSFER' || coa.id === 'OTHER')) return coa.id;
  return categoryIdFromQCSCategory(coa);
};

const flattenChartOfAccountNodes = (coaNodes: ChartOfAccountsTree): ChartOfAccountProps[] => {
  const result: ChartOfAccountProps[] = [];
  coaNodes.forEach((node) => {
    if (node.coa) result.push(node.coa);
    if (node.children) result.push(...flattenChartOfAccountNodes(node.children));
  });
  return result;
};

export const expandChartOfAccountSelections = (
  categories: ReadonlyArray<ChartOfAccountProps>,
  coaNodes: ChartOfAccountsTree,
): ChartOfAccountProps[] => {
  const coaIndex = createChartOfAccountIndex(coaNodes);
  if (coaIndex.byNodeId.size === 0) return [...categories];

  const expanded = categories.flatMap((category): ChartOfAccountProps[] => {
    const nodeId = getFilterCategoryIdFromChartOfAccount(category);
    const isTopLevelSentinel = isTopLevelCategoryFilterId(nodeId);
    const selectedNode = isTopLevelSentinel
      ? coaIndex.byNodeId.get(nodeId)
      : coaIndex.byCoa.get(getChartOfAccountKey(category));
    if (!selectedNode) return [category];
    if (isTopLevelSentinel) {
      return getTopLevelCategoryDescendantIds(nodeId, coaIndex).flatMap((descendantId): ChartOfAccountProps[] => {
        const descendant = coaIndex.byNodeId.get(descendantId)?.coa;
        return descendant ? [descendant] : [];
      });
    }
    return flattenChartOfAccountNodes([selectedNode]);
  });

  return [...new Map(expanded.map((coa) => [`${coa.type}:${coa.id}`, coa])).values()];
};

export const convertQCSCategoriesToChartOfAccounts = (
  selectedCoas: ReportConfigChartOfAccountFilterItemProps['chartOfAccounts'],
  coaNodes: ChartOfAccountsTree,
): ChartOfAccountProps[] => {
  const coaIndex = createChartOfAccountIndex(coaNodes);
  const filterCategoryIds = convertQCSCategoriesToFilterCategoriesWithIndex(selectedCoas, coaIndex);
  const selectedByNodeId = new Map(
    filterCategoryIds.map((id) => [id, getChartOfAccountForFilterCategoryId(id, coaIndex)]),
  );
  const result: ChartOfAccountProps[] = [];

  selectedCoas.forEach((selectedCoa) => {
    if (selectedCoa.type !== 'USAGE_TYPE') {
      result.push({ id: selectedCoa.id, type: selectedCoa.type });
      return;
    }

    const topLevelCategoryId = QCS_USAGE_TYPE_TO_FILTER_CATEGORY[selectedCoa.id];
    if (!topLevelCategoryId) return;

    const descendantIds = getTopLevelCategoryDescendantIds(topLevelCategoryId, coaIndex);
    const descendantCoas = descendantIds.flatMap((id): ChartOfAccountProps[] => {
      const coa = selectedByNodeId.get(id);
      return coa ? [coa] : [];
    });
    if (descendantCoas.length === 0) {
      result.push(TOP_LEVEL_CATEGORY_FORM_COAS[topLevelCategoryId]);
      return;
    }

    result.push(...descendantCoas);
  });

  return [...new Map(result.map((coa) => [`${coa.type}:${coa.id}`, coa])).values()];
};

export const convertChartOfAccountsToQCSCategories = (
  categories: ReadonlyArray<ChartOfAccountProps>,
  coaNodes: ChartOfAccountsTree,
): ReportConfigChartOfAccountFilterItemProps['chartOfAccounts'] =>
  convertFilterCategoriesToQCSCategoriesWithIndex(
    ImmutableSet(categories.map(getFilterCategoryIdFromChartOfAccount)),
    true,
    createChartOfAccountIndex(coaNodes),
  );

const convertFilterCustAndProjToQCSCustAndProj = (
  customersAndProjectsSelected: ImmutableSet<CustomerAndProjects> | CustomerAndProjects[],
) => {
  const result: { customer: string; projects: string[] }[] = [];
  customersAndProjectsSelected?.map((item: CustomerAndProjects) => {
    result.push({
      customer: item.customer?.id || '',
      projects: item.projects?.map((project) => project?.id || ''),
    });
  });
  return result;
};

const convertQCSCustAndProjToFilterCustAndProj = (custAndProjs: { customer: string; projects: string[] }[]) => {
  const clientsAndProjects: string[] = [];
  const customersAndProjectsSelected: CustomerAndProjects[] = [];
  let length = 0;

  if (store) {
    const customersFromStore = customersSelectors.getCustomers(store?.getState());
    const projectsFromStore = projectsSelectors.getProjects(store?.getState());

    custAndProjs.map(({ customer, projects }) => {
      const custObj = customersFromStore.find((cust) => cust?.id === customer) as Customer;

      const projObjs = projects.reduce((acc, projId) => {
        const match = projectsFromStore.find((proj) => proj.id === projId);
        if (match) {
          acc.push(match as Project);
        } else {
          acc.push(mkProject({ id: '0' }));
          length += 1; // Increment length for the '0' project
        }
        return acc;
      }, [] as Project[]);

      customersAndProjectsSelected.push({ customer: custObj, projects: projObjs });

      // create a comma-separated string that starts with the custObj id with `:0` appended followed by a list of all project ids
      const clientsAndProjs = [
        `${custObj?.id}`,
        ...projObjs.map((proj) => (proj?.id !== '0' ? `${proj?.id}` : `${custObj?.id}:0`)),
      ].join(',');
      clientsAndProjects.push(...clientsAndProjs.split(','));
    });
  }

  return {
    clientsAndProjects: [...new Set(clientsAndProjects)],
    customersAndProjectsSelected,
    length,
  };
};

const hasAdvancedFilterValues = (advanced: FilterObject['advanced']): boolean =>
  Object.values(advanced?.toObject?.() ?? {}).some((value) => value !== undefined && value !== null);

export const convertFilterObjectToQCSFilter = (filter: FilterObject): Filter => {
  if (filter?.businesses?.size === 0 && filter?.numFilterItems === 0)
    return mkFilter({ filterItems: [], type: 'REPORT' });

  const qcsFilter: FilterProps = { filterItems: [], type: 'REPORT' };
  if (filter?.categories?.size) {
    const coaNodes = store ? chartOfAccountsSelectors.getChartOfAccountsTree(store.getState()) : [];
    const itemArr = convertFilterCategoriesToQCSCategories(filter.categories, false, coaNodes).filter(
      isChartOfAccountProps,
    );
    qcsFilter?.filterItems?.push({ chartOfAccounts: itemArr, type: 'CHARTOFACCOUNT' });
  }
  if (filter?.accounts?.size) {
    const itemArr = filter.accounts.toArray().map((acc) => ({ id: acc }));
    qcsFilter?.filterItems?.push({ accounts: itemArr, type: 'ACCOUNT' });
  }
  if (filter?.tags?.size) {
    const itemArr = filter.tags.toArray().map((tag) => ({ id: tag }));
    qcsFilter?.filterItems?.push({ tags: itemArr, type: 'TAG' });
  }
  if (filter?.payeeNames?.size) {
    const itemArr = filter.payeeNames.toArray().map((payee) => payee);
    qcsFilter?.filterItems?.push({ payees: itemArr, type: 'PAYEE' });
  }
  if (filter?.businesses?.size) {
    const itemArr = filter.businesses.toArray().map((business) => ({
      type: business === 'PERSONAL' ? 'PERSONAL' : 'BUSINESS',
      id: business === 'PERSONAL' ? '0' : business,
    }));
    qcsFilter?.filterItems?.push({ usages: itemArr, type: 'USAGE' });
  }
  if (filter?.customersAndProjectsSelected?.size) {
    const itemArr = convertFilterCustAndProjToQCSCustAndProj(filter.customersAndProjectsSelected);
    qcsFilter?.filterItems?.push({ customerProjects: itemArr, type: 'CUSTOMERPROJECT' });
  }

  qcsFilter.id = filter.id;
  const returnFitler = mkFilter(qcsFilter);

  return returnFitler;
};

export const convertFilterObjectToReportConfigFilters = (filter: FilterObject): ReportConfigFilters => {
  if (filter?.businesses?.size === 0 && filter?.numFilterItems === 0)
    return mkReportConfigFilters({ filterItems: [], type: 'REPORT' });

  const reportConfigFilter: ReportConfigFilterProps = { filterItems: [], type: 'REPORT' };

  if (filter?.categories?.size) {
    const coaNodes = store ? chartOfAccountsSelectors.getChartOfAccountsTree(store.getState()) : [];
    const itemArr = convertFilterCategoriesToQCSCategories(filter.categories, true, coaNodes);
    reportConfigFilter?.filterItems?.push({ chartOfAccounts: itemArr, type: 'CHARTOFACCOUNT' });
  }
  if (filter?.accounts?.size) {
    const itemArr = filter.accounts.toArray().map((acc) => ({ id: acc }));
    reportConfigFilter?.filterItems?.push({ accounts: itemArr, type: 'ACCOUNT' });
  }
  if (filter?.tags?.size) {
    const itemArr = filter.tags.toArray().map((tag) => ({ id: tag }));
    reportConfigFilter?.filterItems?.push({ tags: itemArr, type: 'TAG' });
  }
  if (filter?.payeeNames?.size) {
    const itemArr = filter.payeeNames.toArray().map((payee) => payee);
    reportConfigFilter?.filterItems?.push({ payees: itemArr, type: 'PAYEE' });
  }
  if (filter?.businesses?.size) {
    const itemArr = filter.businesses.toArray().map((business) => ({
      type: business === 'PERSONAL' ? 'PERSONAL' : 'BUSINESS',
      id: business === 'PERSONAL' ? '0' : business,
    }));
    reportConfigFilter?.filterItems?.push({ usages: itemArr, type: 'USAGE' });
  }
  if (filter?.customersAndProjectsSelected?.size) {
    const itemArr = convertFilterCustAndProjToQCSCustAndProj(filter.customersAndProjectsSelected);
    reportConfigFilter?.filterItems?.push({ customerProjects: itemArr, type: 'CUSTOMERPROJECT' });
  }
  const advanced = filter.advanced;
  if (advanced && hasAdvancedFilterValues(advanced)) {
    const advancedFilter: ReportConfigAdvancedFilterItemProps = {
      type: 'ADVANCED',
      billsAndSubscriptions: advanced.get('isBillOrSubscription'),
      excludedInReports: advanced.get('isExcludedFromReports'),
      excludedInF2S: advanced.get('isExcludedFromF2S'),
      reviewed: advanced.get('isReviewed'),
    };
    reportConfigFilter?.filterItems?.push(advancedFilter);
  }

  reportConfigFilter.id = filter.id;
  const returnFilter = mkReportConfigFilters(reportConfigFilter);

  return returnFilter;
};

const isReportConfigAdvancedFilterItem = (item: any): item is ReportConfigAdvancedFilterItemProps => {
  return (
    item &&
    'type' in item &&
    item.type === 'ADVANCED' &&
    ('billsAndSubscriptions' in item || 'excludedInReports' in item || 'excludedInF2S' in item || 'reviewed' in item)
  );
};

const convertReportConfigAdvancedFilter = (item: ReportConfigAdvancedFilterItemProps, mutable: any): number => {
  const advancedFilter = AdvancedFilterObject({
    isBillOrSubscription: item.billsAndSubscriptions,
    isExcludedFromReports: item.excludedInReports,
    isExcludedFromF2S: item.excludedInF2S,
    isReviewed: item.reviewed,
  });
  mutable.set('advanced', advancedFilter);
  return 1;
};

export const convertQCSFilterToFilterObject = (filter?: Filter | ReportConfigFilters) => {
  const retFilterObj = mkFilterObject({ id: filter?.id ?? undefined });
  let numItems = 0;

  if (!filter) {
    return retFilterObj;
  }

  const coaNodes = store ? chartOfAccountsSelectors.getChartOfAccountsTree(store.getState()) : [];

  return retFilterObj.withMutations((mutable) => {
    filter?.filterItems?.forEach((item) => {
      // Check if this is an advanced filter item from ReportConfigFilters
      if (isReportConfigAdvancedFilterItem(item)) {
        numItems += convertReportConfigAdvancedFilter(item, mutable);
        return;
      }

      switch (item.type) {
        case 'USAGE':
          if (item.usages) {
            mutable.set(
              'businesses',
              ImmutableSet(item.usages.map((it) => (it.type === 'PERSONAL' ? 'PERSONAL' : it.id))),
            );
          }
          break;
        case 'ACCOUNT':
          if (item.accounts) {
            mutable.set('accounts', ImmutableSet(item.accounts.map((it) => it.id)));
            numItems += item.accounts.length;
          }
          break;
        case 'CHARTOFACCOUNT':
          if (item.chartOfAccounts) {
            const filterCategories = convertQCSCategoriesToFilterCategories(item.chartOfAccounts, coaNodes);
            mutable.set('categories', ImmutableSet(filterCategories));
            numItems += item.chartOfAccounts.length;
          }
          break;
        case 'TAG':
          if (item.tags) {
            mutable.set('tags', ImmutableSet(item.tags.map((it) => it.id)));
            numItems += item.tags.length;
          }
          break;
        case 'PAYEE':
          if (item.payees) {
            mutable.set('payeeNames', ImmutableSet(item.payees));
            numItems += item.payees.length;
          }
          break;
        case 'CUSTOMERPROJECT':
          if (item.customerProjects) {
            const { clientsAndProjects, customersAndProjectsSelected, length } =
              convertQCSCustAndProjToFilterCustAndProj(item.customerProjects);
            mutable.set('clientsAndProjects', ImmutableSet(clientsAndProjects));
            mutable.set('customersAndProjectsSelected', ImmutableSet(customersAndProjectsSelected));
            numItems += length;
          }
          break;
        default:
          break;
      }
    });
    mutable.set('numFilterItems', numItems);
  });
};
